import { useEffect, useState } from "react";
import { Menu, Phone, X, Instagram, MessageCircle, Facebook } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CLINIC } from "@/lib/clinic";
import { buildWhatsAppLink } from "@/components/site/WhatsApp";

const LINKS = [
  { href: "#services", label: "Services", id: "services" },
  { href: "#about", label: "About", id: "about" },
  { href: "#clinic-tour", label: "Clinic", id: "clinic-tour" },
  { href: "#doctor", label: "Doctors", id: "doctor" },
  { href: "#reviews", label: "Reviews", id: "reviews" },
  { href: "#contact", label: "Contact", id: "contact" },
];

export default function Navigation({ waNumber }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const waLink = buildWhatsAppLink(waNumber || CLINIC.phoneDial);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navigation"
      className={`fixed top-0 inset-x-0 z-50 transition-all ${
        scrolled ? "glass-nav border-b border-stone-200/70" : "bg-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-16 md:h-20">
        <a
          href="#top"
          data-testid="brand-logo"
          className="flex items-center gap-2.5 group shrink-0"
        >
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#2D4A3E] text-white font-display font-bold text-sm">
            CS
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-display font-semibold text-[#1C221F] text-base sm:text-lg tracking-tight">
              City Smiles
            </span>
            <span className="hidden sm:inline text-[10px] uppercase tracking-[0.22em] text-[#57605B] mt-0.5">
              Dental Clinic
            </span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-1">
          {LINKS.map((l) => (
            <a
              key={l.id}
              href={l.href}
              data-testid={`nav-link-${l.id}`}
              className="px-4 py-2 text-sm text-[#1C221F]/80 hover:text-[#2D4A3E] rounded-full hover:bg-[#2D4A3E]/5 transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <a
            href={CLINIC.instagram}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="btn-instagram-nav"
            aria-label="Instagram"
            className="w-10 h-10 rounded-full border border-[#2D4A3E]/20 text-[#2D4A3E] hover:bg-[#2D4A3E] hover:text-white flex items-center justify-center transition-colors"
          >
            <Instagram className="w-4 h-4" strokeWidth={1.75} />
          </a>
          <a
            href={CLINIC.facebook}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="btn-facebook-nav"
            aria-label="Facebook"
            className="w-10 h-10 rounded-full border border-[#2D4A3E]/20 text-[#2D4A3E] hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2] flex items-center justify-center transition-colors"
          >
            <Facebook className="w-4 h-4" strokeWidth={1.75} />
          </a>
          <Button
            asChild
            variant="outline"
            className="rounded-full border-[#2D4A3E]/25 hover:bg-[#2D4A3E]/5 text-[#2D4A3E]"
          >
            <a href={`tel:${CLINIC.phoneDial}`} data-testid="btn-call-nav">
              <Phone className="w-4 h-4 mr-2" strokeWidth={1.75} />
              {CLINIC.phoneDisplay}
            </a>
          </Button>
          <Button
            asChild
            className="rounded-full bg-[#25D366] hover:bg-[#1FBA5A] text-white"
          >
            <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="btn-whatsapp-nav">
              <MessageCircle className="w-4 h-4 mr-2" strokeWidth={2} />
              WhatsApp
            </a>
          </Button>
          <Button
            asChild
            className="rounded-full bg-[#2D4A3E] hover:bg-[#21362D] text-white"
          >
            <a href="#contact" data-testid="btn-book-nav">Book Appointment</a>
          </Button>
        </div>

        <button
          className="md:hidden p-2 rounded-full hover:bg-[#2D4A3E]/5"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="btn-mobile-menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-stone-200 bg-white/95 backdrop-blur-xl">
          <div className="container-x py-4 flex flex-col gap-1">
            {LINKS.map((l) => (
              <a
                key={l.id}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`nav-link-mobile-${l.id}`}
                className="px-3 py-3 text-base text-[#1C221F] rounded-xl hover:bg-[#2D4A3E]/5"
              >
                {l.label}
              </a>
            ))}
            <div className="flex gap-2 pt-2">
              <Button asChild variant="outline" className="flex-1 rounded-full border-[#2D4A3E]/25 text-[#2D4A3E]">
                <a href={`tel:${CLINIC.phoneDial}`} data-testid="btn-call-mobile">
                  <Phone className="w-4 h-4 mr-2" strokeWidth={1.75} />Call
                </a>
              </Button>
              <Button asChild className="flex-1 rounded-full bg-[#2D4A3E] hover:bg-[#21362D] text-white">
                <a href="#contact" onClick={() => setOpen(false)} data-testid="btn-book-mobile">Book</a>
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
